/**
 * YC demo — Brain → Company.
 *
 * Single source of truth for the fictional org Lantern is hiring for.
 * Every page in /brain/company/* reads from here, and downstream demo
 * surfaces (chats, agentic flow) should reference these same numbers
 * so the story stays coherent on stage.
 */

export type CompanyStage = 'pre-seed' | 'seed' | 'series-a' | 'series-b' | 'series-c' | 'growth';

export interface OrgProfile {
    name: string;
    legalName: string;
    domain: string;
    tagline: string;
    mission: string;
    stage: CompanyStage;
    foundedYear: number;
    hqCity: string;
    hqCountry: string;
    remotePolicy: string;
    teamSize: number;
    arrBand: string;
    lastFundingRound: {
        amount: string;
        round: string;
        leadInvestor: string;
        coInvestors: string[];
        closedAt: string; // ISO
    };
    icp: {
        whoWeServe: string;
        wedgeProduct: string;
        differentiation: string;
    };
    industry: string;
    valuesShortlist: { title: string; body: string }[];
}

export interface HiringPhilosophy {
    bar: string;
    nonNegotiables: string[];
    calibrations: { rule: string; rationale: string; addedBy: string; addedAt: string }[];
    dealbreakers: { signal: string; reason: string }[];
    interviewLoopPrinciples: string[];
}

export interface HeadcountTeam {
    teamId: string;
    teamName: string;
    teamLead: string;
    headcountStart: number;
    plannedHires: number;
    filled: number;
    open: number;
    quarters: { q: 'Q1' | 'Q2' | 'Q3' | 'Q4'; planned: number; filled: number; openRoles: string[] }[];
    notes?: string;
}

export interface HeadcountPlan {
    fiscalYear: number;
    asOfDate: string; // ISO
    startingHeadcount: number;
    endingHeadcountTarget: number;
    totalPlannedHires: number;
    annualBudgetUsd: number;
    teams: HeadcountTeam[];
}

export interface PastHire {
    id: string;
    name: string;
    avatarInitials: string;
    role: string;
    team: 'Engineering' | 'GTM' | 'Product' | 'Design' | 'G&A' | 'Operations';
    seniority: 'Junior' | 'Mid' | 'Senior' | 'Staff' | 'Principal' | 'Lead' | 'Director';
    hiredAt: string; // ISO
    tenureMonths: number;
    status: 'active' | 'departed';
    sourceChannel: 'Referral' | 'Outbound' | 'Inbound' | 'Network' | 'Agency' | 'Boomerang';
    sourcedBy: string;
    previousCompany: string;
    pastCompanies: string[];
    rampWeeks: number; // how long to productivity
    perfTier?: 'top-10' | 'strong' | 'meets' | 'below';
    notes?: string;
}

export const NORTHWIND_ORG: OrgProfile = {
    name: 'Northwind',
    legalName: 'Northwind Technologies, Inc.',
    domain: 'northwind.co',
    tagline: 'Payments infrastructure for software companies that ship.',
    mission:
        'Make moving money inside a SaaS product feel like a single line of code. Ledger primitives, embedded payouts, and a live console — built for product engineers, not finance teams.',
    stage: 'series-b',
    foundedYear: 2022,
    hqCity: 'San Francisco',
    hqCountry: 'United States',
    remotePolicy: 'HQ in SF · remote-friendly across US & EU · 2 days in-office for SF-based ICs',
    teamSize: 47,
    arrBand: '$14M ARR (Q1 2026, +118% YoY)',
    lastFundingRound: {
        amount: '$42M',
        round: 'Series B',
        leadInvestor: 'Sequoia Capital',
        coInvestors: ['Index Ventures', 'BoxGroup', 'Stripe (strategic)'],
        closedAt: '2025-11-04',
    },
    icp: {
        whoWeServe: 'Series A–C vertical SaaS companies (5–200 engineers) that need to move money on behalf of their customers — marketplaces, B2B platforms, fintech embedders.',
        wedgeProduct: 'Embedded payouts with built-in compliance, sub-100ms ledger writes, and a console engineers can hand a CFO without translation.',
        differentiation:
            'We are the only payouts API where the ledger and the console are the same product — which means CS, ops, and engineering see exactly what the customer sees. No reconciliations.',
    },
    industry: 'Fintech infrastructure (B2B SaaS)',
    valuesShortlist: [
        {
            title: 'Ship for one customer at a time',
            body: 'Generality earns its keep. We build for a real customer in front of us, then carefully widen.',
        },
        {
            title: 'Reverse the polarity',
            body: 'When a process gets painful, we ask whether the painful party is the right one. Often they are not.',
        },
        {
            title: 'Show, do not tell',
            body: 'Internal docs are demos. Loom > Notion. Working code > slide.',
        },
        {
            title: 'Calm at the wire',
            body: 'When the money moves, the team is quietest. Drama is not a virtue.',
        },
    ],
};

export const NORTHWIND_PHILOSOPHY: HiringPhilosophy = {
    bar: 'We hire people who would be considered "the bar" at the next stage — i.e. someone we would happily promote a level if they joined today. Athletes over experts; willingness to widen scope is a stronger signal than depth in any one tool.',
    nonNegotiables: [
        'Owns work end-to-end — including parts that are not in the JD.',
        'Writes things down. We are async-first across two timezones.',
        'Has at least one "tasted the floor" story — a project that did not work and what they did with it.',
        'No PIPs in last 12 months at any past employer (verified through references, not screening).',
    ],
    calibrations: [
        {
            rule: 'For Founding AE roles: weight "comfort with ambiguity" higher than "polished discovery process."',
            rationale:
                'After 4 hires, polished sellers from late-stage orgs ramped 2.1× slower than scrappy mid-stage hires. Justin formalized this from the Crypto.com calibration.',
            addedBy: 'Justin Tang',
            addedAt: '2026-04-29',
        },
        {
            rule: 'For Senior+ Backend Eng: -1 modifier if no production sharding or queue ownership.',
            rationale:
                'Rails-monolith experience does not translate to our Postgres-replica + queues setup. Sam down-scored two candidates rated 4 to 3, both reversals validated post-hire.',
            addedBy: 'Sam Lee',
            addedAt: '2026-04-28',
        },
        {
            rule: 'Reweight ML sourcing: ICs from infra-native companies (Cohere, Anyscale, Modal) +30%; FAANG-only profiles 0%.',
            rationale: 'Our 3 strongest Staff ML hires came from infra-native shops, not FAANG. Stack assumptions match.',
            addedBy: 'Thomas Wong',
            addedAt: '2026-04-26',
        },
        {
            rule: 'Tenure flags (>3 jobs in 18 months): investigate via reference, do not auto-deduct.',
            rationale:
                '4 of our last 6 strong hires had short stints due to acquisitions or pivots. False-rejection rate dropped 22% after switching from auto-deduct.',
            addedBy: 'Avery Patel',
            addedAt: '2026-04-25',
        },
    ],
    dealbreakers: [
        {
            signal: 'Cannot describe the worst on-call incident they personally caused',
            reason: 'Either no production exposure or unwilling to own — both disqualifying for Senior+.',
        },
        {
            signal: 'Comp expectations >35% above band with no countervailing signal',
            reason: 'Usually predicts tenure <12 months; not worth the ramp cost at our stage.',
        },
        {
            signal: 'Public commentary that conflates "moving fast" with "no docs"',
            reason: 'We move fast specifically because we write things down. Cultural mismatch.',
        },
    ],
    interviewLoopPrinciples: [
        'No more than 5 hours of candidate time before an offer decision.',
        'Every loop ends with a paid trial day or a 90-min "deal review" / "system review" with a founder.',
        'Reference calls happen before final round, not after — they steer the loop, not validate it.',
        'Take-homes are capped at 4 hours and are paid; we read every submission.',
    ],
};

export const NORTHWIND_HEADCOUNT: HeadcountPlan = {
    fiscalYear: 2026,
    asOfDate: '2026-05-02',
    startingHeadcount: 47,
    endingHeadcountTarget: 63,
    totalPlannedHires: 16,
    annualBudgetUsd: 4_180_000,
    teams: [
        {
            teamId: 'eng',
            teamName: 'Engineering',
            teamLead: 'Sam Lee',
            headcountStart: 19,
            plannedHires: 8,
            filled: 3,
            open: 5,
            quarters: [
                { q: 'Q1', planned: 2, filled: 2, openRoles: [] },
                {
                    q: 'Q2',
                    planned: 3,
                    filled: 1,
                    openRoles: ['Senior Frontend Engineer (×2)', 'Staff ML Engineer'],
                },
                {
                    q: 'Q3',
                    planned: 2,
                    filled: 0,
                    openRoles: ['Senior Backend Engineer', 'Infra Engineer'],
                },
                { q: 'Q4', planned: 1, filled: 0, openRoles: ['Engineering Manager — Platform'] },
            ],
            notes: 'Frontend bench is the bottleneck; payments-domain experience scarce in market.',
        },
        {
            teamId: 'gtm',
            teamName: 'GTM',
            teamLead: 'Justin Tang',
            headcountStart: 11,
            plannedHires: 6,
            filled: 2,
            open: 4,
            quarters: [
                { q: 'Q1', planned: 1, filled: 1, openRoles: [] },
                {
                    q: 'Q2',
                    planned: 2,
                    filled: 1,
                    openRoles: ['Founding AE — Strategic'],
                },
                {
                    q: 'Q3',
                    planned: 2,
                    filled: 0,
                    openRoles: ['Sales Engineer', 'AE — Mid-Market'],
                },
                { q: 'Q4', planned: 1, filled: 0, openRoles: ['Head of Partnerships'] },
            ],
            notes: 'Most behind plan. Pipeline review with Sequoia in 3 weeks.',
        },
        {
            teamId: 'product-design',
            teamName: 'Product & Design',
            teamLead: 'Devi Krishnan',
            headcountStart: 6,
            plannedHires: 1,
            filled: 0,
            open: 1,
            quarters: [
                { q: 'Q1', planned: 0, filled: 0, openRoles: [] },
                { q: 'Q2', planned: 0, filled: 0, openRoles: [] },
                { q: 'Q3', planned: 1, filled: 0, openRoles: ['Product Designer — Console'] },
                { q: 'Q4', planned: 0, filled: 0, openRoles: [] },
            ],
        },
        {
            teamId: 'gna',
            teamName: 'G&A',
            teamLead: 'Avery Patel',
            headcountStart: 11,
            plannedHires: 1,
            filled: 0,
            open: 1,
            quarters: [
                { q: 'Q1', planned: 0, filled: 0, openRoles: [] },
                { q: 'Q2', planned: 1, filled: 0, openRoles: ['Compliance Lead'] },
                { q: 'Q3', planned: 0, filled: 0, openRoles: [] },
                { q: 'Q4', planned: 0, filled: 0, openRoles: [] },
            ],
            notes: 'Compliance Lead is regulatory-driven; tied to Series B disclosure commitments.',
        },
    ],
};

export const NORTHWIND_PAST_HIRES: PastHire[] = [
    {
        id: 'hire-001',
        name: 'Maya Chen',
        avatarInitials: 'MC',
        role: 'Recruiting Lead',
        team: 'G&A',
        seniority: 'Senior',
        hiredAt: '2024-08-12',
        tenureMonths: 21,
        status: 'active',
        sourceChannel: 'Referral',
        sourcedBy: 'Thomas Wong',
        previousCompany: 'Sino Group',
        pastCompanies: ['Sino Group', 'Hyatt', 'Crypto.com'],
        rampWeeks: 6,
        perfTier: 'top-10',
        notes:
            'Originally surfaced from the Crypto.com AE shortlist Justin and Lantern calibrated on. Now runs all of recruiting.',
    },
    {
        id: 'hire-002',
        name: 'Sam Lee',
        avatarInitials: 'SL',
        role: 'Engineering Manager',
        team: 'Engineering',
        seniority: 'Lead',
        hiredAt: '2023-03-06',
        tenureMonths: 38,
        status: 'active',
        sourceChannel: 'Network',
        sourcedBy: 'Thomas Wong',
        previousCompany: 'Stripe',
        pastCompanies: ['Stripe', 'Square', 'Asana'],
        rampWeeks: 4,
        perfTier: 'top-10',
    },
    {
        id: 'hire-003',
        name: 'Devi Krishnan',
        avatarInitials: 'DK',
        role: 'Hiring Manager — Eng',
        team: 'Engineering',
        seniority: 'Director',
        hiredAt: '2024-01-22',
        tenureMonths: 28,
        status: 'active',
        sourceChannel: 'Outbound',
        sourcedBy: 'Maya Chen',
        previousCompany: 'Linear',
        pastCompanies: ['Linear', 'Vercel', 'Figma'],
        rampWeeks: 5,
        perfTier: 'strong',
    },
    {
        id: 'hire-004',
        name: 'Avery Patel',
        avatarInitials: 'AP',
        role: 'People Ops Lead',
        team: 'G&A',
        seniority: 'Lead',
        hiredAt: '2023-09-18',
        tenureMonths: 32,
        status: 'active',
        sourceChannel: 'Boomerang',
        sourcedBy: 'Justin Tang',
        previousCompany: 'Hyatt',
        pastCompanies: ['Hyatt', 'WeWork', 'Lever'],
        rampWeeks: 7,
        perfTier: 'strong',
    },
    {
        id: 'hire-005',
        name: 'Kenji Sato',
        avatarInitials: 'KS',
        role: 'Head of Talent',
        team: 'G&A',
        seniority: 'Director',
        hiredAt: '2024-11-04',
        tenureMonths: 18,
        status: 'active',
        sourceChannel: 'Network',
        sourcedBy: 'Devi Krishnan',
        previousCompany: 'Modal',
        pastCompanies: ['Modal', 'Anyscale', 'Cohere'],
        rampWeeks: 5,
        perfTier: 'top-10',
        notes: 'Brought the infra-native ML sourcing playbook with him; informed the Staff ML calibration.',
    },
    {
        id: 'hire-006',
        name: 'Jordan Aoki',
        avatarInitials: 'JA',
        role: 'Senior Backend Engineer',
        team: 'Engineering',
        seniority: 'Senior',
        hiredAt: '2025-02-11',
        tenureMonths: 14,
        status: 'active',
        sourceChannel: 'Inbound',
        sourcedBy: 'Sam Lee',
        previousCompany: 'Plaid',
        pastCompanies: ['Plaid', 'Two Sigma'],
        rampWeeks: 8,
        perfTier: 'strong',
    },
    {
        id: 'hire-007',
        name: 'Priya Shah',
        avatarInitials: 'PS',
        role: 'Sr. Frontend Engineer',
        team: 'Engineering',
        seniority: 'Senior',
        hiredAt: '2025-06-30',
        tenureMonths: 10,
        status: 'active',
        sourceChannel: 'Outbound',
        sourcedBy: 'Maya Chen',
        previousCompany: 'Wise',
        pastCompanies: ['Wise', 'SoundCloud'],
        rampWeeks: 6,
        perfTier: 'top-10',
        notes: 'The Slack ping that kicks off the agentic-setup demo is from Priya — she now manages the FE bench.',
    },
    {
        id: 'hire-008',
        name: 'Tomás Reyes',
        avatarInitials: 'TR',
        role: 'Founding AE',
        team: 'GTM',
        seniority: 'Senior',
        hiredAt: '2024-10-14',
        tenureMonths: 19,
        status: 'active',
        sourceChannel: 'Outbound',
        sourcedBy: 'Justin Tang',
        previousCompany: 'Crypto.com',
        pastCompanies: ['Crypto.com', 'Robinhood'],
        rampWeeks: 11,
        perfTier: 'top-10',
        notes: 'First hire under the new "deal review with CEO" loop — replaced the case-study stage. Ramped 38% faster than control.',
    },
    {
        id: 'hire-009',
        name: 'Hannah Voss',
        avatarInitials: 'HV',
        role: 'Staff ML Engineer',
        team: 'Engineering',
        seniority: 'Staff',
        hiredAt: '2025-01-08',
        tenureMonths: 16,
        status: 'active',
        sourceChannel: 'Network',
        sourcedBy: 'Kenji Sato',
        previousCompany: 'Cohere',
        pastCompanies: ['Cohere', 'Anyscale'],
        rampWeeks: 6,
        perfTier: 'strong',
    },
    {
        id: 'hire-010',
        name: 'Rohan Mistry',
        avatarInitials: 'RM',
        role: 'Senior Frontend Engineer',
        team: 'Engineering',
        seniority: 'Senior',
        hiredAt: '2025-09-22',
        tenureMonths: 7,
        status: 'active',
        sourceChannel: 'Inbound',
        sourcedBy: 'Priya Shah',
        previousCompany: 'Stripe',
        pastCompanies: ['Stripe', 'Asana', 'Palantir'],
        rampWeeks: 5,
        perfTier: 'strong',
    },
    {
        id: 'hire-011',
        name: 'Lena Hoffmann',
        avatarInitials: 'LH',
        role: 'Product Designer',
        team: 'Design',
        seniority: 'Senior',
        hiredAt: '2024-04-29',
        tenureMonths: 25,
        status: 'active',
        sourceChannel: 'Referral',
        sourcedBy: 'Devi Krishnan',
        previousCompany: 'Figma',
        pastCompanies: ['Figma', 'Notion'],
        rampWeeks: 4,
        perfTier: 'top-10',
    },
    {
        id: 'hire-012',
        name: 'Marcus Hadid',
        avatarInitials: 'MH',
        role: 'AE — Mid-Market',
        team: 'GTM',
        seniority: 'Senior',
        hiredAt: '2024-06-18',
        tenureMonths: 23,
        status: 'departed',
        sourceChannel: 'Agency',
        sourcedBy: 'Avery Patel',
        previousCompany: 'Salesforce',
        pastCompanies: ['Salesforce', 'Outreach'],
        rampWeeks: 16,
        perfTier: 'meets',
        notes:
            'Polished late-stage seller; ramped 2.1× slower than the founder-led-loop hires. Departure informed the "ambiguity > polish" calibration.',
    },
    {
        id: 'hire-013',
        name: 'Iris Nakamura',
        avatarInitials: 'IN',
        role: 'Senior Backend Engineer',
        team: 'Engineering',
        seniority: 'Senior',
        hiredAt: '2025-04-02',
        tenureMonths: 13,
        status: 'active',
        sourceChannel: 'Inbound',
        sourcedBy: 'Sam Lee',
        previousCompany: 'Modal',
        pastCompanies: ['Modal', 'Datadog'],
        rampWeeks: 5,
        perfTier: 'top-10',
    },
    {
        id: 'hire-014',
        name: 'Ben Okafor',
        avatarInitials: 'BO',
        role: 'Sales Engineer',
        team: 'GTM',
        seniority: 'Senior',
        hiredAt: '2025-08-11',
        tenureMonths: 9,
        status: 'active',
        sourceChannel: 'Network',
        sourcedBy: 'Tomás Reyes',
        previousCompany: 'Plaid',
        pastCompanies: ['Plaid', 'Brex'],
        rampWeeks: 7,
        perfTier: 'strong',
    },
];
